/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Geometry of the product tour: where the card goes and how its target is kept in view. */

/** Below this width the card is a bottom sheet (the stylesheet docks it). */
export const SHEET_BELOW = 600;
/** Room between the card and the viewport edge, and between the card and its target. */
const EDGE = 12;
const GAP = 14;
/** Spotlight padding around the target. */
export const PAD = 4;

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const boxOf = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/**
 * Where the card goes beside `target`: right, below, left, then above, taking the first spot that
 * does not cover the target, each clamped inside the viewport. When none fits (a target as big as
 * the screen) the first clamped spot wins.
 */
export function placeCard(target: Box, card: { width: number; height: number }) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const right = target.left + target.width;
  const bottom = target.top + target.height;
  const candidates: [number, number][] = [
    [right + GAP, target.top + target.height / 2 - card.height / 2],
    [target.left + target.width / 2 - card.width / 2, bottom + GAP],
    [target.left - GAP - card.width, target.top + target.height / 2 - card.height / 2],
    [target.left + target.width / 2 - card.width / 2, target.top - GAP - card.height],
  ];
  const clamp = ([x, y]: [number, number]): [number, number] => [
    Math.max(EDGE, Math.min(x, vw - card.width - EDGE)),
    Math.max(EDGE, Math.min(y, vh - card.height - EDGE)),
  ];
  const covers = ([x, y]: [number, number]) =>
    !(
      x + card.width <= target.left - PAD ||
      x >= right + PAD ||
      y + card.height <= target.top - PAD ||
      y >= bottom + PAD
    );
  const spots = candidates.map(clamp);
  const [left, top] = spots.find((spot) => !covers(spot)) ?? spots[0];
  return { left: Math.round(left), top: Math.round(top) };
}

/** The nearest ancestor that scrolls vertically. */
function scrollParent(el: Element): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (/(auto|scroll|overlay)/.test(overflowY) && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * On a phone the card is a bottom sheet; scroll the target up so it sits above the sheet rather
 * than under it (never scrolling its top out of view). A target at the very end of its page has
 * nothing left to scroll by, so the page gets that much room underneath for as long as the stop
 * shows. Returns the undo, which gives the scroller its own bottom padding back.
 */
export function liftAboveSheet(el: Element, sheetTop: number): () => void {
  const r = el.getBoundingClientRect();
  const overlap = r.bottom - (sheetTop - EDGE);
  const scroller = overlap > 0 ? scrollParent(el) : null;
  if (!scroller) return () => {};
  const lift = Math.min(overlap, Math.max(0, r.top - EDGE));
  const spare = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
  const own = scroller.style.paddingBottom;
  if (lift > spare) {
    const base = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
    scroller.style.paddingBottom = `${base + lift - spare}px`;
  }
  scroller.scrollTop += lift;
  return () => {
    scroller.style.paddingBottom = own;
  };
}

/**
 * Whether the phone sheet should dock at the top for this stop: the target still sits under a
 * bottom sheet (a footer action or a floating button at the foot of a page that does not scroll)
 * and is low enough for a sheet across the top to leave it clear.
 */
export function dockSheetTop(el: Element, sheetHeight: number): boolean {
  const r = el.getBoundingClientRect();
  const underSheet = r.bottom + PAD > window.innerHeight - sheetHeight;
  return underSheet && r.top - PAD >= sheetHeight;
}
